import { zodResolver } from "@hookform/resolvers/zod";
import {
  KikCustomerPasswordUpdateFields as CustomerPasswordUpdateFields,
  kikCustomerPasswordUpdateSchema as customerPasswordUpdateSchema,
  KikUserZodSchemas as ZodSchemas,
} from "@kikocosmeticsorg/uc-api-nest-common-fe";
import { useTranslations } from "next-intl";
import { Dispatch, SetStateAction, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import Button from "~/components/common/button";
import Checkbox, { CheckboxVariants } from "~/components/form/checkbox";
import Errors from "~/components/form/errors";
import InputField from "~/components/form/input-field";
import { useAuthContext } from "~/contexts/auth";
import { useBreakpoint } from "~/contexts/breakpoint";
import Logger from "~/services/logger/logger";

import Dialog from "../../common/dialog";
import Icon from "../../common/icon";
import editPasswordDialogStyles from "./edit-password.module.scss";
import styles from "./settings.module.scss";

type Props = {
  isOpen?: boolean;
  onClose: () => void;
  onAfterOpen?: () => void;
  onAfterClose?: () => void;
  setEditPasswordSuccess?: Dispatch<SetStateAction<boolean>>;
};

export default function EditPassword({
  isOpen = false,
  onAfterOpen,
  onAfterClose,
  onClose,
  setEditPasswordSuccess,
}: Props) {
  const { isLoading, authError, passwordChange } = useAuthContext();
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState<boolean>(false);
  const t = useTranslations();
  const breakpoint = useBreakpoint();

  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<CustomerPasswordUpdateFields>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldFocusError: false,
    resolver: zodResolver(customerPasswordUpdateSchema),
  });

  const onSubmit: SubmitHandler<CustomerPasswordUpdateFields> = async (data: CustomerPasswordUpdateFields) => {
    passwordChange(data)
      .then(() => {
        setEditPasswordSuccess?.(true);
        onClose();
      })
      .catch((err) => Logger.instance.error("EditPassword errored > ", err));
  };

  return (
    <Dialog
      isOpen={isOpen}
      title={
        <>
          <Icon name="edit" />
          <span>{t("generic.myaccount.settings.changePassword")}</span>
        </>
      }
      onClose={onClose}
      onAfterOpen={onAfterOpen}
      onAfterClose={onAfterClose}
      customStyles={editPasswordDialogStyles}
    >
      <p className={styles.editSettingsFormDescription}>
        {t("generic.myaccount.settings.changePasswordSubtitle", {
          minCharacters: ZodSchemas.passwordMinlength,
        })}
      </p>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.editSettingsForm}>
        <InputField
          {...register("oldPassword")}
          className={styles.inputFieldContainer}
          type={showOldPassword ? "text" : "password"}
          label={t("generic.form.current_password_label")}
          onClearErrors={() => clearErrors("oldPassword")}
          error={
            errors.oldPassword?.message
              ? t(errors.oldPassword?.message, {
                  fieldName: t("generic.form.current_password_label"),
                  minLength: ZodSchemas.passwordMinlength,
                })
              : ""
          }
          buttonName={
            <Checkbox
              label={t("generic.form.show_hide_password")}
              variant={CheckboxVariants.visibility}
              width={16}
              height={16}
              checked={showOldPassword}
              onChange={() => setShowOldPassword(!showOldPassword)}
              customStyles={{
                labelCheckbox: styles.labelCheckbox,
                container: styles.buttonCheckbox,
              }}
            />
          }
        />
        <InputField
          {...register("password")}
          className={styles.inputFieldContainer}
          type={showNewPassword ? "text" : "password"}
          label={t("generic.form.new_password_label")}
          onClearErrors={() => clearErrors("password")}
          error={
            errors.password?.message
              ? t(errors.password?.message, {
                  fieldName: t("generic.form.new_password_label"),
                  minLength: ZodSchemas.passwordMinlength,
                })
              : ""
          }
          buttonName={
            <Checkbox
              checked={showNewPassword}
              onChange={() => setShowNewPassword(!showNewPassword)}
              label={t("generic.form.show_hide_password")}
              variant={CheckboxVariants.visibility}
              width={16}
              height={16}
              customStyles={{
                labelCheckbox: styles.labelCheckbox,
                container: styles.buttonCheckbox,
              }}
            />
          }
        />
        <InputField
          {...register("confirmPassword")}
          className={styles.inputFieldContainer}
          type={showConfirmNewPassword ? "text" : "password"}
          label={t("generic.repeatNewPassword")}
          onClearErrors={() => clearErrors("confirmPassword")}
          error={
            errors.confirmPassword?.message
              ? t(errors.confirmPassword?.message, {
                  fieldName: t("generic.repeatNewPassword"),
                  newPasswordFieldName: t("generic.form.new_password_label"),
                  confirmNewPasswordFieldName: t("generic.repeatNewPassword"),
                  minLength: ZodSchemas.passwordMinlength,
                })
              : ""
          }
          buttonName={
            <Checkbox
              checked={showConfirmNewPassword}
              onChange={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
              label={t("generic.form.show_hide_password")}
              variant={CheckboxVariants.visibility}
              width={16}
              height={16}
              customStyles={{
                labelCheckbox: styles.labelCheckbox,
                container: styles.buttonCheckbox,
              }}
            />
          }
        />

        <Errors error={authError} style={{ gridColumn: breakpoint === "desktop" ? "2 span" : "1" }} />

        <Button className={styles.submitModal} variant="secondary" type="submit" loading={isLoading}>
          {t("generic.myaccount.settings.updatePasswordCta")}
        </Button>
      </form>
    </Dialog>
  );
}
